
// src/services/OrderContext.jsx - FIXED
import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import { 
  createOrder,
  getUserOrders,
  getOrderById,
  updateOrderStatus
} from '../services/OrderService';

// Create context
const OrderContext = createContext();

// Create provider
export const OrderProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userOrders, setUserOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load user orders when user changes
  useEffect(() => {
    const loadUserOrders = async () => {
      if (currentUser) {
        try {
          setLoading(true);
          const orders = await getUserOrders(currentUser.uid);
          setUserOrders(orders);
          setError(null);
        } catch (error) {
          console.error('Error loading user orders:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      } else {
        setUserOrders([]);
      }
    };

    loadUserOrders();
  }, [currentUser]);

  // Create a new order
  const placeOrder = async (cartId, orderData) => {
    try {
      if (!cartId) {
        throw new Error('Cart ID is required to place an order');
      }
      
      setLoading(true);
      const newOrder = await createOrder(cartId, orderData);
      
      // Update user orders
      setUserOrders(prevOrders => [newOrder, ...prevOrders]);
      
      return newOrder;
    } catch (error) {
      console.error('Error placing order:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get order details by ID
  const getOrderDetails = async (orderId) => {
    try {
      if (!orderId) {
        throw new Error('Order ID is required');
      }
      
      setLoading(true);
      const orderDetails = await getOrderById(orderId);
      return orderDetails;
    } catch (error) {
      console.error('Error getting order details:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Value to be provided to consumers
  const value = {
    userOrders,
    loading,
    error,
    placeOrder,
    getOrderDetails,
    refreshOrders: async () => {
      if (currentUser) {
        try {
          setLoading(true);
          const orders = await getUserOrders(currentUser.uid);
          setUserOrders(orders);
          setError(null);
        } catch (error) {
          console.error('Error refreshing orders:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

// Custom hook to use the order context
export const useOrders = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;




